// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { formatHumanName } from '@medplum/core';
import type { Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import {
  INLAB_REFERRAL_MODULE_URI,
  isTestPatient,
  PATIENT_TRIAGE_STATUS,
  type PatientReferralStatus,
  PERFORMER_EXTENSION_URL,
  POLARIS_INSURANCE_ELIGIBILITY_STATUS_TAG_SYSTEM,
  POLARIS_REFERRAL_STATUS_TAG_SYSTEM,
  POLARIS_TRIAGE_STATUS_TAG_SYSTEM,
  type TriageSortState,
} from '@polarissleep/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';

const DENIED_TAG = `${POLARIS_TRIAGE_STATUS_TAG_SYSTEM}|${PATIENT_TRIAGE_STATUS.denied}`;
const HSAT_ELIGIBILITY_MODULE_URI = 'http://polarissleep.com/guidance/hsat-eligibility';

export interface DeniedEntry {
  patientId: string | undefined;
  patientName: string;
  compositionId: string | undefined;
  intakeCompletedDate: string | undefined;
  reason: string | undefined;
  eligibilityStatusCode: string | undefined;
  triageStatusCode: string | undefined;
  evaluatedById: string | undefined;
  evaluatedBy: string | undefined;
  evaluatedOn: string | undefined;
  referralStatus: PatientReferralStatus | undefined;
  referralNotes: string | undefined;
  isTestPatient: boolean;
}

interface GRResult {
  occurrenceDateTime?: string;
  moduleUri?: string;
  reasonCode?: { coding?: { display?: string }[] }[];
  note?: { text?: string }[];
  extension?: {
    url?: string;
    valueReference?: { resource?: { id?: string; name?: { given?: string[]; family?: string }[] } };
  }[];
}

interface PatientResult {
  id?: string;
  name?: { given?: string[]; family?: string }[];
  meta?: { tag?: { system?: string; code?: string }[] };
  CompositionList?: { id?: string; date?: string }[];
  GuidanceResponseList?: GRResult[];
}

const PATIENT_ENTRY_FIELDS = `
  id
  name { given family }
  meta { tag { system code } }
  CompositionList(_reference: subject status: "final" _count: 1 _sort: "-_lastUpdated") {
    id
    date
  }
  # v1 ceiling: inlab-referral GRs are unbounded; 10 covers 9 referral saves before HSAT GR falls off
  GuidanceResponseList(_reference: subject _count: 10 _sort: "-_lastUpdated") {
    occurrenceDateTime
    moduleUri
    reasonCode { coding { display } }
    note { text }
    extension {
      url
      valueReference {
        resource {
          ... on Practitioner { id name { given family } }
        }
      }
    }
  }
`;

/** @internal — exported for unit testing only; do not call outside this module. */
export function mapPatientToEntry(patient: PatientResult): DeniedEntry {
  const patientName = patient.name?.[0] ? formatHumanName(patient.name[0]) : 'Unknown Patient';
  const composition = patient.CompositionList?.[0];
  const gr = patient.GuidanceResponseList?.find((g) => g.moduleUri === HSAT_ELIGIBILITY_MODULE_URI);
  const performerExt = gr?.extension?.find((e) => e.url === PERFORMER_EXTENSION_URL);
  const practitioner = performerExt?.valueReference?.resource;
  const evaluatedBy = practitioner?.name?.[0] ? formatHumanName(practitioner.name[0]) : undefined;
  const eligibilityTag = patient.meta?.tag?.find((t) => t.system === POLARIS_INSURANCE_ELIGIBILITY_STATUS_TAG_SYSTEM);
  const referralStatusTag = patient.meta?.tag?.find((t) => t.system === POLARIS_REFERRAL_STATUS_TAG_SYSTEM);
  const referralNoteGr = patient.GuidanceResponseList?.find((g) => g.moduleUri === INLAB_REFERRAL_MODULE_URI);
  const triageTag = patient.meta?.tag?.find((t) => t.system === POLARIS_TRIAGE_STATUS_TAG_SYSTEM);
  return {
    patientId: patient.id,
    patientName,
    compositionId: composition?.id,
    intakeCompletedDate: composition?.date,
    reason: gr?.reasonCode?.[0]?.coding?.[0]?.display,
    eligibilityStatusCode: eligibilityTag?.code,
    triageStatusCode: triageTag?.code,
    evaluatedById: practitioner?.id,
    evaluatedBy,
    evaluatedOn: gr?.occurrenceDateTime,
    referralStatus: referralStatusTag?.code as PatientReferralStatus | undefined,
    referralNotes: referralNoteGr?.note?.[0]?.text,
    isTestPatient: isTestPatient(patient as Patient | undefined),
  };
}

export function useDeniedQueue({
  page,
  pageSize,
  sort,
  search,
  hideTestPatients,
}: {
  page: number;
  pageSize: number;
  sort?: TriageSortState;
  search?: string;
  hideTestPatients?: boolean;
}): { entries: DeniedEntry[]; loading: boolean; total: number; refetch: () => void } {
  const medplum = useMedplum();
  const [entries, setEntries] = useState<DeniedEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const sortKey = useMemo(() => (sort ? `${sort.column}|${sort.direction}` : null), [sort]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: sort tracked via sortKey (stable primitive)
  const fetchEntries = useCallback(async () => {
    setLoading(true);
    const offset = (page - 1) * pageSize;

    try {
      let sortParam: string;
      if (!sort || sort.column !== 'name') {
        sortParam = '-_lastUpdated';
      } else {
        sortParam = sort.direction === 'desc' ? '-given' : 'given';
      }

      const searchPredicate = search ? ` name: "${search}"` : '';

      const query = `
        query DeniedQueue {
          PatientList(_tag: "${DENIED_TAG}"${searchPredicate} _count: ${pageSize} _offset: ${offset} _sort: "${sortParam}") {
            ${PATIENT_ENTRY_FIELDS}
          }
          PatientConnection(_tag: "${DENIED_TAG}"${searchPredicate}) { count }
        }
      `;

      const result = await medplum.graphql(query);
      const list = (result.data?.PatientList ?? []) as PatientResult[];
      const connection = result.data?.PatientConnection as { count: number } | undefined;
      setTotal(connection?.count ?? 0);
      const mapped = list.map(mapPatientToEntry);
      const toSet = hideTestPatients ? mapped.filter((e) => !e.isTestPatient) : mapped;
      setEntries(toSet);
    } finally {
      setLoading(false);
    }
  }, [medplum, page, pageSize, sortKey, search, hideTestPatients]);

  useEffect(() => {
    fetchEntries().catch(console.error);
  }, [fetchEntries]);

  return { entries, loading, total, refetch: fetchEntries };
}
